/**
 * Custom Hooks for SSP Modeller
 */

import { useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { DEFAULT_COHORTS, DEFAULT_SICKNESS_PATTERN } from '../data/constants';

const STORAGE_KEY = 'ssp_modeller_scenarios';

/**
 * Hook for managing saved scenarios
 */
export function useScenarios() {
  const [scenarios, setScenarios] = useState([]);
  const [currentScenarioId, setCurrentScenarioId] = useState(null);

  // Load scenarios from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setScenarios(parsed);
      }
    } catch (error) {
      console.error('Failed to load scenarios:', error);
    }
  }, []);

  // Save scenarios to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(scenarios));
    } catch (error) {
      console.error('Failed to save scenarios:', error);
    }
  }, [scenarios]);

  const saveScenario = useCallback((name, data) => {
    const id = uuidv4();
    const newScenario = {
      id,
      name,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      data,
    };
    
    setScenarios(prev => [...prev, newScenario]);
    setCurrentScenarioId(id);
    return id;
  }, []);

  const updateScenario = useCallback((id, data) => {
    setScenarios(prev => prev.map(s => 
      s.id === id 
        ? { ...s, data, updatedAt: new Date().toISOString() }
        : s
    ));
  }, []);

  const deleteScenario = useCallback((id) => {
    setScenarios(prev => prev.filter(s => s.id !== id));
    if (currentScenarioId === id) {
      setCurrentScenarioId(null);
    }
  }, [currentScenarioId]);

  const loadScenario = useCallback((id) => {
    const scenario = scenarios.find(s => s.id === id);
    if (scenario) {
      setCurrentScenarioId(id);
      return scenario.data;
    }
    return null;
  }, [scenarios]);

  const duplicateScenario = useCallback((id) => {
    const scenario = scenarios.find(s => s.id === id);
    if (scenario) {
      return saveScenario(`${scenario.name} (Copy)`, { ...scenario.data });
    }
    return null;
  }, [scenarios, saveScenario]);

  return {
    scenarios,
    currentScenarioId,
    saveScenario,
    updateScenario,
    deleteScenario,
    loadScenario,
    duplicateScenario,
    setCurrentScenarioId,
  };
}

/**
 * Hook for managing cohort state
 */
export function useCohorts(initialCohorts = DEFAULT_COHORTS) {
  const [cohorts, setCohorts] = useState(
    initialCohorts.map((c, i) => ({ ...c, id: c.id || `cohort-${i}` }))
  );

  const addCohort = useCallback((cohort = {}) => {
    const newCohort = {
      id: uuidv4(),
      name: cohort.name || `New Cohort`,
      description: cohort.description || '',
      count: cohort.count || 10,
      contractedHours: cohort.contractedHours || 20,
      avgPickupHours: cohort.avgPickupHours || 0,
      hourlyRate: cohort.hourlyRate || 11.44,
      qualifyingDays: cohort.qualifyingDays || 5,
      typicalShiftLength: cohort.typicalShiftLength || 6,
      rotaProbability: cohort.rotaProbability || 0.5,
    };
    setCohorts(prev => [...prev, newCohort]);
    return newCohort.id;
  }, []);

  const updateCohort = useCallback((id, updates) => {
    setCohorts(prev => prev.map(c => 
      c.id === id ? { ...c, ...updates } : c
    ));
  }, []);

  const removeCohort = useCallback((id) => {
    setCohorts(prev => prev.filter(c => c.id !== id));
  }, []);

  const resetCohorts = useCallback(() => {
    setCohorts(DEFAULT_COHORTS.map((c, i) => ({ ...c, id: c.id || `cohort-${i}` })));
  }, []);

  return {
    cohorts,
    setCohorts,
    addCohort,
    updateCohort,
    removeCohort,
    resetCohorts,
  };
}

/**
 * Hook for managing sickness pattern state
 */
export function useSicknessPattern(initialPattern = DEFAULT_SICKNESS_PATTERN) {
  const [pattern, setPattern] = useState(initialPattern);

  const updateDistribution = useCallback((key, value) => {
    setPattern(prev => ({
      ...prev,
      distribution: {
        ...prev.distribution,
        [key]: Math.max(0, Math.min(100, parseFloat(value) || 0)),
      },
    }));
  }, []);

  const updateAvgLength = useCallback((key, value) => {
    setPattern(prev => ({
      ...prev,
      avgLengths: {
        ...prev.avgLengths,
        [key]: Math.max(1, parseFloat(value) || 1),
      },
    }));
  }, []);

  const updateEpisodes = useCallback((value) => {
    setPattern(prev => ({
      ...prev,
      avgEpisodesPerEmployee: Math.max(0, parseFloat(value) || 0),
    }));
  }, []);

  const resetPattern = useCallback(() => {
    setPattern(DEFAULT_SICKNESS_PATTERN);
  }, []);

  const distributionTotal = Object.values(pattern.distribution).reduce((a, b) => a + b, 0);

  return {
    pattern,
    setPattern,
    updateDistribution,
    updateAvgLength,
    updateEpisodes,
    resetPattern,
    distributionTotal,
    isValidDistribution: Math.abs(distributionTotal - 100) < 0.1,
  };
}

/**
 * Hook for managing backfill options
 */
export function useBackfillOptions() {
  const [options, setOptions] = useState({
    includeBackfill: true,
    method: 'same-rate',
    agencyMarkup: 1.3,
    overtimePremium: 1.25,
    customRate: null,
  });

  const updateOption = useCallback((key, value) => {
    setOptions(prev => ({ ...prev, [key]: value }));
  }, []);

  return {
    options,
    setOptions,
    updateOption,
  };
}

/**
 * Hook for individual calculator state
 */
export function useIndividualCalculator() {
  const [employee, setEmployee] = useState({
    id: '',
    contractedHours: 20,
    avgPickupHours: 4,
    hourlyRate: 11.50,
    qualifyingDays: 5,
    typicalShiftLength: 6,
    payFrequency: 'weekly', // 'weekly' | 'monthly'
    weeksEmployed: 8, // For new employees (<8 weeks)
  });

  const [earnings, setEarnings] = useState([0, 0, 0, 0, 0, 0, 0, 0]);
  const [absence, setAbsence] = useState({
    totalDays: 5,
    rosteredDays: 3,
    isLinkedPeriod: false,
    previousSSPWeeks: 0, // Track cumulative SSP weeks for 28-week maximum
  });

  const updateEmployee = useCallback((updates) => {
    setEmployee(prev => ({ ...prev, ...updates }));
  }, []);

  const updateEarning = useCallback((weekIndex, value) => {
    setEarnings(prev => {
      const updated = [...prev];
      updated[weekIndex] = Math.max(0, parseFloat(value) || 0);
      return updated;
    });
  }, []);

  const updateAbsence = useCallback((updates) => {
    setAbsence(prev => ({ ...prev, ...updates }));
  }, []);

  const calculateFromPattern = useCallback(() => {
    // Calculate expected weekly earnings based on employee setup
    const weeklyHours = employee.contractedHours + employee.avgPickupHours;
    const expectedWeekly = weeklyHours * employee.hourlyRate;
    
    // Generate 8 weeks with some realistic variation
    const newEarnings = Array(8).fill(0).map(() => {
      const variation = (Math.random() - 0.5) * 0.3; // ±15% variation
      return Math.max(0, expectedWeekly * (1 + variation));
    });
    
    setEarnings(newEarnings);
  }, [employee]);

  const reset = useCallback(() => {
    setEmployee({
      id: '',
      contractedHours: 20,
      avgPickupHours: 4,
      hourlyRate: 11.50,
      qualifyingDays: 5,
      typicalShiftLength: 6,
      payFrequency: 'weekly',
      weeksEmployed: 8,
    });
    setEarnings([0, 0, 0, 0, 0, 0, 0, 0]);
    setAbsence({
      totalDays: 5,
      rosteredDays: 3,
      isLinkedPeriod: false,
      previousSSPWeeks: 0,
    });
  }, []);

  return {
    employee,
    earnings,
    absence,
    updateEmployee,
    updateEarning,
    updateAbsence,
    calculateFromPattern,
    reset,
    setEmployee,
    setEarnings,
    setAbsence,
  };
}
